import _ from "lodash";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import DataGrid, { Column, RowsChangeData, SortColumn } from "react-data-grid";
import ActionCard from "./ActionCard";
import ActivityCard from "./ActivityCard";
import CompanyCard from "./CompanyCard";
import PersonCard from "./PersonCard";
import moment from "moment";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { v4 as uuidv4 } from "uuid";

import { IonList, IonText } from "@ionic/react";
import {
  putUserAction,
  UserActionType,
  UserActionEntityType,
  PersonExternal,
  Activity,
} from "../api/Nunchi";
import { Tooltip } from "@mui/material";

import "@aws-amplify/ui-react/styles.css";
import "react-data-grid/lib/styles.css";
import "./PersonList.css";
import Logo from "./Logo";

type Row =
  | {
      type: "NORMAL";
      name: string;
      title: string;
      company: string;
      location: string;
      activity_time: string;
      activity_body: string;
      activity_url: string;
      entity_url: string;
      company_url: string;
      expanded: boolean;
      column: string;
      person: any;
    }
  | {
      type: "DETAIL";
      column: string;
      person: any;
    };

export enum BROWSER_ENUM {
  EDGE,
  INTERNET_EXPLORER,
  FIRE_FOX,
  OPERA,
  UC_BROWSER,
  SAMSUNG_BROWSER,
  CHROME,
  SAFARI,
  UNKNOWN,
}

type Nullable<T> = T | null;

const PersonList = ({
  persons,
  entityToActivitiesMap,
  filters,
}: {
  persons: Map<string, PersonExternal>;
  entityToActivitiesMap: Map<string, Activity[]>;
  filters: { [key: string]: string };
}) => {
  const [rows, setRows] = useState<Row[]>([]);
  const [filteredRows, setFilteredRows] = useState<Row[]>([]);
  const [personKeys, setPersonKeys] = useState<Set<string>>(new Set());
  const [rowHeight, setRowHeight] = useState<number>(45);
  const [sortColumns, setSortColumns] = useState<readonly SortColumn[]>([]);
  const { user } = useAuthenticator((context) => [context.user]);
  const onSortColumnsChange = useCallback((sortColumns: SortColumn[]) => {
    setSortColumns(sortColumns.slice(-1));
  }, []);
  const dataGridRef = useRef<any>(null);
  const [activityWidth, setActivityWidth] = useState<Nullable<number>>(null);

  useEffect(() => {
    const userAgent = (regexp: RegExp): boolean =>
      regexp.test(window.navigator.userAgent);
    function detectBrowser(): BROWSER_ENUM {
      switch (true) {
        case userAgent(/edg/i):
          return BROWSER_ENUM.EDGE;
        case userAgent(/trident/i):
          return BROWSER_ENUM.INTERNET_EXPLORER;
        case userAgent(/firefox|fxios/i):
          return BROWSER_ENUM.FIRE_FOX;
        case userAgent(/opr\//i):
          return BROWSER_ENUM.OPERA;
        case userAgent(/ucbrowser/i):
          return BROWSER_ENUM.UC_BROWSER;
        case userAgent(/samsungbrowser/i):
          return BROWSER_ENUM.SAMSUNG_BROWSER;
        case userAgent(/chrome|chromium|crios/i):
          return BROWSER_ENUM.CHROME;
        case userAgent(/safari/i):
          return BROWSER_ENUM.SAFARI;
        default:
          return BROWSER_ENUM.UNKNOWN;
      }
    }
    const BROWSER: BROWSER_ENUM = detectBrowser();
    const isChrome = BROWSER === BROWSER_ENUM.CHROME;
    // Chrome specific logic as dynamic width for a column does not work
    if (isChrome) {
      if (
        dataGridRef.current &&
        dataGridRef.current.element.offsetWidth > 1185
      ) {
        setActivityWidth(dataGridRef.current.element.offsetWidth - 695);
      } else {
        setActivityWidth(500);
      }
    } else {
      setActivityWidth(null);
    }
  }, [dataGridRef.current]);

  useEffect(() => {
    if (persons) {
      if (persons.size === 0) {
        setPersonKeys(new Set());
        setRows([]);
        setFilteredRows([]);
      } else {
        const newRows: Row[] = rows;
        persons.forEach((value: PersonExternal, key: string) => {
          let tagline = value.job_title;
          if (value.company_name) {
            tagline += " at " + value.company_name;
          }
          if (!personKeys.has(key)) {
            const articleBody =
              (value.activities[0].activity_type === "SHARE" ||
                value.activities[0].activity_type === "REPOST") &&
              !value.activities[0].article_body
                ? value.activities[0].parent_article_body
                : value.activities[0].article_body;
            newRows.push({
              type: "NORMAL",
              name: value.name,
              title: tagline || "",
              company: value.company_name,
              location: value.location,
              activity_time:
                value.activities.length > 0
                  ? value.activities[0].activity_time
                  : "",
              activity_body: articleBody,
              activity_url:
                value.activities.length > 0 ? value.activities[0].url : "",
              entity_url: value.person_id,
              company_url: value.company ? value.company.website_url : "",
              expanded: false,
              column: "",
              person: value,
            });
            personKeys.add(key);
          }
        });
        setPersonKeys(personKeys);
        setRows(newRows);
        setFilteredRows(newRows);
      }
    }
  }, [persons]);

  useEffect(() => {
    if (sortColumns.length > 0) {
      const { columnKey, direction } = sortColumns[0];
      let sortedRows: Row[] = [
        ...rows
          .filter((row: Row) => row.type === "NORMAL")
          .map((row: Row) => ({ ...row, expanded: false })),
      ];
      switch (columnKey) {
        default:
          sortedRows = sortedRows.sort((a, b) =>
            a[columnKey as keyof Row].localeCompare(b[columnKey as keyof Row])
          );
      }
      const newRows = direction === "DESC" ? sortedRows.reverse() : sortedRows;
      setRows(newRows);
      setFilteredRows(newRows);
    }
  }, [sortColumns]);

  useEffect(() => {
    setFilteredRows(
      rows.filter((row: Row) => {
        {
          if (row.type === "DETAIL") {
            return false;
          }
          const candidateValues = [
            row.name.toLowerCase(),
            row.title.toLowerCase(),
          ];
          const rowActivities = entityToActivitiesMap.get(row.person.person_id);
          if (rowActivities) {
            rowActivities.forEach((x: Activity) => {
              if (x.parent_article_body) {
                candidateValues.push(x.parent_article_body.toLowerCase());
              }
              if (x.article_body) {
                candidateValues.push(x.article_body.toLowerCase());
              }
            });
          }
          return candidateValues.some((v) => v.includes(filters.searchValue));
        }
      })
    );
  }, [filters]);

  const recordUserAction = (entityId: string, entityType: string) => {
    (async function () {
      const actionId = uuidv4();
      await putUserAction(
        user,
        actionId,
        UserActionType.CLICK,
        entityId,
        entityType,
        "",
        ""
      );
    })();
  };

  const renderDetails = (person: any, column: string) => {
    if (column === "name") {
      return <PersonCard person={person} setRowHeight={setRowHeight} />;
    } else if (column === "company") {
      return <CompanyCard person={person} setRowHeight={setRowHeight} />;
    } else if (column === "activity_body") {
      let activities = entityToActivitiesMap.get(person.person_id);
      if (activities === undefined) {
        activities = [];
      }
      return (
        <ActivityCard
          person={person}
          setRowHeight={setRowHeight}
          activities={activities}
        />
      );
    } else if (column === "actions") {
      return <ActionCard person={person} setRowHeight={setRowHeight} />;
    }
  };

  const columns = useMemo((): readonly Column<Row>[] => {
    return [
      {
        key: "name",
        name: "Name",
        resizable: true,
        sortable: true,
        width: 200,
        colSpan(args) {
          return args.type === "ROW" && args.row.type === "DETAIL"
            ? 8
            : undefined;
        },
        cellClass(row) {
          return row.type === "DETAIL" ? "detail-table" : undefined;
        },
        renderCell({ row, onRowChange }) {
          if (row.type === "DETAIL") {
            return renderDetails(row.person, row.column);
          }

          if (!row.entity_url.startsWith("https://")) {
            return <IonText>{row.name}</IonText>;
          }

          return (
            <div>
              <a
                target="_blank"
                rel="noopener noreferrer"
                href={row.entity_url}
                style={{
                  paddingRight: "7px",
                }}
                onClick={() => {
                  recordUserAction(row.entity_url, UserActionEntityType.PERSON);
                }}
              >
                <Logo url={row.entity_url} />
              </a>
              <IonText>{row.name}</IonText>
            </div>
          );
        },
      },
      {
        key: "title",
        name: "Tagline",
        resizable: true,
        sortable: true,
        width: 300,
        renderCell({ row, onRowChange }) {
          if (row.type === "DETAIL") {
            return renderDetails(row.person, row.column);
          }

          return (
            <div>
              <Tooltip title={row.title} placement="bottom-start">
                <IonText>{row.title}</IonText>
              </Tooltip>
            </div>
          );
        },
      },
      /*{
        key: "company",
        name: "Company",
        resizable: true,
        sortable: true,
        width: 200,
        colSpan(args) {
          return args.type === "ROW" && args.row.type === "DETAIL"
            ? 8
            : undefined;
        },
        cellClass(row) {
          return row.type === "DETAIL" ? "detail" : undefined;
        },
        renderCell({ row, onRowChange }) {
          if (row.type === "DETAIL") {
            return renderDetails(row.person, row.column);
          }

          return (
            <div
              className={row.person.company ? "hyperlink" : ""}
              onClick={() => {
                if (row.person.company) {
                  recordUserAction(
                    row.person.person_id,
                    UserActionEntityType.COMPANY
                  );
                  onRowChange({
                    ...row,
                    expanded: !row.expanded,
                    column: "company",
                  });
                }
              }}
            >
              {row.company}
            </div>
          );
        },
      },
      {
        key: "location",
        name: "Location",
        resizable: true,
        sortable: true,
        width: 200,
        renderCell({ row }) {
          if (row.type === "NORMAL") {
            if (!row.location) {
              return null;
            }
            let tokens = row.location.split(",");
            // eg. Austria, at or Fort Lauderdale, Florida, United States, us
            if (tokens[tokens.length - 1].trim().length === 2) {
              tokens = tokens.slice(0, tokens.length - 1);
            }
            return tokens.slice(0, 2).join(",");
          }
        },
      },*/
      {
        key: "activity_body",
        name: "Activity",
        resizable: true,
        width: activityWidth,
        colSpan(args) {
          return args.type === "ROW" && args.row.type === "DETAIL"
            ? 8
            : undefined;
        },
        cellClass(row) {
          return row.type === "DETAIL" ? "detail" : undefined;
        },
        renderCell({ row, onRowChange }) {
          if (row.type === "DETAIL") {
            return renderDetails(row.person, row.column);
          }

          if (!row.activity_body) {
            return null;
          }

          return (
            <div
              onClick={() => {
                recordUserAction(
                  row.person.person_id,
                  UserActionEntityType.ACTIVITIES
                );
                onRowChange({
                  ...row,
                  expanded: !row.expanded,
                  column: "activity_body",
                });
              }}
              style={{
                overflow: "clip",
                textOverflow: "ellipsis",
                cursor: "pointer",
              }}
            >
              <Logo url={row.activity_url} />
              <span style={{ paddingLeft: "7px" }}>{row.activity_body}</span>
            </div>
          );
        },
      },
      {
        key: "activity_time",
        name: "Time",
        resizable: true,
        sortable: true,
        width: 110,
        renderCell({ row }) {
          if (row.type === "NORMAL") {
            if (!row.activity_time) {
              return null;
            }
            return moment(row.activity_time).local().fromNow();
          }
        },
      },
      // {
      //   key: "actions",
      //   name: "Actions",
      //   resizable: true,
      //   width: 75,
      //   colSpan(args) {
      //     return args.type === "ROW" && args.row.type === "DETAIL"
      //       ? 8
      //       : undefined;
      //   },
      //   cellClass(row) {
      //     return row.type === "DETAIL" ? "detail" : undefined;
      //   },
      //   renderCell({ row, onRowChange }) {
      //     if (row.type === "DETAIL") {
      //       return renderDetails(row.person, row.column);
      //     }

      //     return (
      //       <div
      //         onClick={() => {
      //           recordUserAction(
      //             row.person.person_id,
      //             UserActionEntityType.COMPLETION
      //           );
      //           onRowChange({
      //             ...row,
      //             expanded: !row.expanded,
      //             column: "actions",
      //           });
      //         }}
      //         style={{
      //           width: "100%",
      //           height: "100%",
      //           display: "flex",
      //           justifyContent: "center",
      //           alignItems: "center",
      //           cursor: "pointer",
      //         }}
      //       >
      //         <img
      //           alt="chatgpt-action"
      //           width={20}
      //           height={20}
      //           src="data:image/png;base64,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"
      //         />
      //       </div>
      //     );
      //   },
      // },
    ];
  }, [entityToActivitiesMap]);

  function onRowsChange(rows: Row[], { indexes }: RowsChangeData<Row>) {
    const rowIdx = indexes[0];
    const row = rows[rowIdx];
    if (row.type === "NORMAL") {
      if (row.expanded) {
        rows.splice(rowIdx + 1, 0, {
          ...row,
          type: "DETAIL",
        });
        rows = rows
          .map((r: Row, i: number) => ({ ...r, expanded: i === rowIdx }))
          .filter(
            (r: Row, i: number) => r.type === "NORMAL" || i === rowIdx + 1
          );
      } else {
        const columnChanged = row.column !== rows[rowIdx + 1].column;
        rows.splice(rowIdx + 1, 1);
        if (columnChanged) {
          row.expanded = true;
          rows.splice(rowIdx + 1, 0, {
            ...row,
            type: "DETAIL",
          });
        }
      }
      setFilteredRows(rows);
    }
  }

  return (
    <IonList
      color="none"
      lines="none"
      style={{
        background: "transparent",
        paddingTop: "10px",
      }}
    >
      <DataGrid
        ref={dataGridRef}
        className="rdg-light"
        style={{ height: "auto" }}
        columns={columns}
        rows={filteredRows}
        sortColumns={sortColumns}
        onSortColumnsChange={onSortColumnsChange}
        onRowsChange={onRowsChange}
        rowHeight={(args) =>
          (args.row as Row).type === "DETAIL" ? rowHeight : 45
        }
        onCellKeyDown={(_, event) => {
          if (event.isDefaultPrevented()) {
            // skip parent grid keyboard navigation if nested grid handled it
            event.preventGridDefault();
          }
        }}
      />
    </IonList>
  );
};

export default PersonList;
